/**
 * Sitewide "enter once" reveal for page content (never for chrome: header,
 * hero, menus, filters, inputs, drawers, cart, modals). Two targets:
 *
 * - .v-card-reveal — added directly in product-card / collection-card
 *   markup, so every card animates independently as it enters view.
 * - .v-section-reveal — added here via a small allowlist of section IDs,
 *   so a whole content section (title + its content together) fades in
 *   as one unit instead of the title animating separately.
 *
 * transform + opacity only, no layout properties — no reflow, no CLS.
 */

const SECTION_REVEAL_KEYWORDS = [
  'volts_trust_bar',
  'collection_list_QTccKB',
  'product_list_themegen',
  'bento_links_volts',
  'reputation_ml_volts',
  'testimonials_volts',
];

/**
 * Single shared observer (not one per call) so late-arriving content
 * (see observeNewContent below) reuses it instead of spinning up a new
 * IntersectionObserver per batch.
 */
const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  },
  { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
);

/**
 * @param {NodeListOf<Element> | Element[]} elements
 */
function observeReveal(elements) {
  Array.from(elements).forEach((el) => revealObserver.observe(el));
}

function tagSections() {
  const sections = Array.from(document.querySelectorAll('[id^="shopify-section-"]'));
  sections.forEach((section) => {
    if (SECTION_REVEAL_KEYWORDS.some((keyword) => section.id.includes(keyword))) {
      section.classList.add('v-section-reveal');
    }
  });
}

/**
 * Sections/cards loaded after the first pass — e.g. product recommendations
 * on the PDP, morphed in asynchronously once scrolled into view — never
 * went through observeReveal() otherwise, leaving them at opacity:0
 * forever. Mirrors the same MutationObserver pattern already used by
 * assets/product-card-rating.js for infinite-scroll/quick-add cards.
 */
function observeNewContent() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const el = /** @type {Element} */ (node);
        if (el.matches('.v-card-reveal, .v-section-reveal')) observeReveal([el]);
        el.querySelectorAll?.('.v-card-reveal, .v-section-reveal').forEach((child) => observeReveal([child]));
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function run() {
  tagSections();
  observeReveal(document.querySelectorAll('.v-section-reveal, .v-card-reveal'));
  observeNewContent();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run);
} else {
  run();
}
